import { useEffect } from "react";
import { useMemoizedFn } from "ahooks";
import { useSnapshot } from "valtio";
import { isEtfOverlapReference } from "@features/etf-overlap/utils/buildEtfOverlapChatMessage";
import { MemberStatusCardModalCreateAble } from "@components/Chat/MemberStatusCardModal";
import { useChatMemberStatusCard } from "@hooks/useChatMemberStatusCard";
import { useCredits } from "@hooks/useCredits";
import { rootStore } from "@store/rootStore";
import { LOCAL_STORAGE_KEYS } from "@config/constant";

/**
 * 封装发送前的 credit 前置拦截逻辑：
 * - credits <= 0 时弹出会员引导弹窗
 * - 返回 blocked=true 表示本次发送应被阻止
 *
 * Missions 下线（EDG-557，PRD 附录 A.6）后不再引导「去做任务 / 领取积分」，积分不足只走会员弹窗。
 */
export const useChatCreditCheck = () => {
  const { credits } = useCredits();
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const { memberStatus, cardProps, isAppPayingRenewLoading } =
    useChatMemberStatusCard();

  /**
   * 检查 credit 是否充足，不足时弹窗提示。
   * @returns `{ blocked: true }` 表示应阻止发送，`{ blocked: false }` 表示可继续。
   */
  const checkCredit = useMemoizedFn(
    async (question?: string): Promise<{ blocked: boolean }> => {
      // EDG-561：重叠分析的自动发出（协议行 + 空正文）不计额度、不触发付费墙（PRD S4.1 / AC-4.13）。
      // 只放行协议行本身；结果之后用户手动追问仍按线上额度规则。
      if (isEtfOverlapReference(question)) {
        return { blocked: false };
      }
      if (credits && credits > 0) {
        return { blocked: false };
      }
      if (!isLoggedIn) {
        return { blocked: false };
      }

      await MemberStatusCardModalCreateAble({
        memberStatus,
        ...cardProps,
        isAppPayingRenewLoading,
      });

      return { blocked: true };
    },
  );

  useEffect(() => {
    if (credits && credits > 0) {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.HIDDEN_CHAT_ID);
    }
  }, [credits]);

  return {
    checkCredit,
  };
};
